import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_URL } from "../config";

function sessionChip(session) {
    const status = session?.status || "never_registered";
    if (status === "active") return { color: "success", label: session.label };
    if (status === "idle") return { color: "info", label: session.label };
    if (status === "registered") return { color: "warning", label: session.label };
    if (status === "released") return { color: "warning", label: session.label };
    return { color: "default", label: session?.label || "Never registered" };
}

function formatWhen(iso) {
    if (!iso) return "—";
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return "—";
    const mins = Math.round((Date.now() - t) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    if (mins < 1440 * 14) return `${Math.floor(mins / 1440)}d ago`;
    return new Date(t).toLocaleString();
}

function shortDeviceId(id) {
    const value = String(id || "");
    if (value.length <= 14) return value;
    return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function apiErrorText(error, fallback) {
    const code = error.response?.data?.error;
    if (code === "device_id_in_use") return "That Device ID is already bound to another subscriber.";
    if (code === "device_not_found") return "Device record not found. Refresh and try again.";
    if (code === "missing_params") return "Enter a Device ID.";
    if (code === "invalid_device_id") return "Device ID is too long.";
    return code || fallback;
}

function DeviceIdPreview({ deviceId, onView }) {
    if (!deviceId) {
        return (
            <Typography variant="body2" color="text.secondary">
                Not registered
            </Typography>
        );
    }
    return (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
                variant="body2"
                fontFamily="ui-monospace, monospace"
                noWrap
                title={deviceId}
                sx={{ minWidth: 0 }}
            >
                {shortDeviceId(deviceId)}
            </Typography>
            <Button size="small" onClick={onView} startIcon={<VisibilityIcon />} sx={{ flexShrink: 0, minWidth: 0 }}>
                View
            </Button>
        </Stack>
    );
}

export default function SoftwareLicenseDevices() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [planFilter, setPlanFilter] = useState("Monthly");
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [nextDeviceId, setNextDeviceId] = useState("");
    const [releaseRow, setReleaseRow] = useState(null);
    const [viewDevice, setViewDevice] = useState(null);

    const load = useCallback(async () => {
        const { data } = await axios.get(`${API_URL}/software-licenses/devices`, { withCredentials: true });
        setItems(data.items || []);
    }, []);

    useEffect(() => {
        load().catch((error) =>
            setMessage({
                severity: "error",
                text: error.response?.data?.error || "Unable to load subscription devices.",
            })
        );
    }, [load]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((row) => {
            const plan = String(row.planType || "");
            if (planFilter === "Monthly" && plan.toLowerCase() !== "monthly") return false;
            if (planFilter === "Annual" && plan.toLowerCase() !== "annual") return false;
            if (!q) return true;
            const hay = [row.email, row.planName, row.edition, row.device?.deviceId, row.contactId, row.device?.appVersion, row.device?.companyName]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        });
    }, [items, planFilter, search]);

    const stats = useMemo(
        () => ({
            total: visible.length,
            licensed: visible.filter((row) => row.licenseActive).length,
            active: visible.filter((row) => row.session?.status === "active").length,
            registered: visible.filter((row) => row.session?.registered).length,
            never: visible.filter((row) => row.session?.status === "never_registered").length,
        }),
        [visible]
    );

    function openRemap(row) {
        setEditRow(row);
        setNextDeviceId(row.device?.deviceId || "");
        setMessage(null);
    }

    function openRelease(row) {
        setReleaseRow(row);
        setMessage(null);
    }

    async function copyDeviceId(value) {
        try {
            await navigator.clipboard.writeText(value);
            setMessage({ severity: "success", text: "Device ID copied." });
        } catch {
            setMessage({ severity: "error", text: "Could not copy Device ID." });
        }
    }

    async function saveRemap() {
        if (!editRow?.device?.id) return;
        setBusy(true);
        setMessage(null);
        try {
            await axios.patch(
                `${API_URL}/software-licenses/devices/${editRow.device.id}`,
                { deviceId: nextDeviceId.trim() },
                { withCredentials: true }
            );
            setMessage({
                severity: "success",
                text: `Device ID remapped for ${editRow.email}. They can register or reopen MachineFlow on the new PC.`,
            });
            setEditRow(null);
            await load();
        } catch (error) {
            setMessage({
                severity: "error",
                text: apiErrorText(error, "Unable to remap device ID."),
            });
        } finally {
            setBusy(false);
        }
    }

    async function confirmRelease() {
        if (!releaseRow?.device?.id) return;
        setBusy(true);
        setMessage(null);
        try {
            await axios.post(
                `${API_URL}/software-licenses/devices/${releaseRow.device.id}/release`,
                {},
                { withCredentials: true }
            );
            setMessage({
                severity: "success",
                text: `Device released for ${releaseRow.email}. They can register a new PC.`,
            });
            setReleaseRow(null);
            await load();
        } catch (error) {
            setMessage({
                severity: "error",
                text: apiErrorText(error, "Unable to release device."),
            });
        } finally {
            setBusy(false);
        }
    }

    function rowActions(row, iconOnly) {
        const canEdit = Boolean(row.device?.id);
        const canRelease = row.device?.status === "active";
        if (iconOnly) {
            return (
                <Stack direction="row" spacing={0.25}>
                    <IconButton
                        size="small"
                        aria-label="Remap device"
                        disabled={!canEdit || busy}
                        onClick={() => openRemap(row)}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="warning"
                        aria-label="Release device"
                        disabled={!canRelease || busy}
                        onClick={() => openRelease(row)}
                    >
                        <LinkOffIcon fontSize="small" />
                    </IconButton>
                </Stack>
            );
        }
        return (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Button size="small" startIcon={<EditIcon />} disabled={!canEdit || busy} onClick={() => openRemap(row)}>
                    Remap
                </Button>
                <Button
                    size="small"
                    color="warning"
                    startIcon={<LinkOffIcon />}
                    disabled={!canRelease || busy}
                    onClick={() => openRelease(row)}
                >
                    Release
                </Button>
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
                    <Typography color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                Monthly (and other online) subscribers bind a Device ID when they register MachineFlow. Remap it
                when they move to a new PC, or release the binding so they can register again. App version and
                &quot;in session&quot; update while MachineFlow is open (heartbeat every few minutes).
            </Typography>

            {message && <Alert severity={message.severity}>{message.text}</Alert>}

            <Stack spacing={1.25}>
                <TextField
                    size="small"
                    label="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth={isMobile}
                    sx={{ maxWidth: { md: 320 } }}
                />
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                >
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={planFilter}
                        onChange={(_e, value) => {
                            if (value) setPlanFilter(value);
                        }}
                    >
                        <ToggleButton value="Monthly">Monthly</ToggleButton>
                        <ToggleButton value="Annual">Annual</ToggleButton>
                        <ToggleButton value="All">All</ToggleButton>
                    </ToggleButtonGroup>
                    <IconButton aria-label="Refresh" onClick={() => load()} disabled={busy}>
                        <RefreshIcon />
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                        {stats.total} shown · {stats.licensed} licensed · {stats.active} in session · {stats.never} never
                    </Typography>
                </Stack>
            </Stack>

            {isMobile ? (
                <Stack spacing={1.25}>
                    {!visible.length && (
                        <Typography color="text.secondary">No subscription licenses match this filter.</Typography>
                    )}
                    {visible.map((row) => {
                        const session = sessionChip(row.session);
                        return (
                            <Card key={row.licenseId} variant="outlined">
                                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                            <Box sx={{ minWidth: 0, pr: 1 }}>
                                                <Typography fontWeight={700} noWrap title={row.email}>
                                                    {row.email}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {row.planName || row.edition || "—"}
                                                    {row.planType ? ` · ${row.planType}` : ""}
                                                </Typography>
                                                {row.device?.appVersion && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        App {row.device.appVersion}
                                                        {row.device.companyName ? ` · ${row.device.companyName}` : ""}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {rowActions(row, true)}
                                        </Stack>
                                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                            <Chip
                                                size="small"
                                                color={row.licenseActive ? "success" : "default"}
                                                label={row.licenseActive ? "Active" : row.reason || "Inactive"}
                                            />
                                            <Chip size="small" color={session.color} label={session.label} />
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                            <DeviceIdPreview
                                                deviceId={row.device?.deviceId}
                                                onView={() => setViewDevice(row)}
                                            />
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {formatWhen(row.session?.lastSeen)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
            ) : (
                <Card>
                    <CardContent sx={{ overflowX: "auto" }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Plan</TableCell>
                                        <TableCell>License</TableCell>
                                        <TableCell>App version</TableCell>
                                        <TableCell>Device ID</TableCell>
                                        <TableCell>Session</TableCell>
                                        <TableCell>Last seen</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!visible.length && (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography color="text.secondary">
                                                    No subscription licenses match this filter.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {visible.map((row) => {
                                        const session = sessionChip(row.session);
                                        return (
                                            <TableRow key={row.licenseId} hover>
                                                <TableCell>
                                                    <Typography fontWeight={700}>{row.email}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {row.planName || row.edition || "—"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {[row.edition, row.planType].filter(Boolean).join(" · ") || "—"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={row.licenseActive ? "success" : "default"}
                                                        label={row.licenseActive ? "Active" : row.reason || "Inactive"}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontFamily="ui-monospace, monospace">
                                                        {row.device?.appVersion || "—"}
                                                    </Typography>
                                                    {row.device?.companyName ? (
                                                        <Typography variant="caption" color="text.secondary" noWrap title={row.device.companyName}>
                                                            {row.device.companyName}
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 220 }}>
                                                    <DeviceIdPreview
                                                        deviceId={row.device?.deviceId}
                                                        onView={() => setViewDevice(row)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip size="small" color={session.color} label={session.label} />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        title={
                                                            row.session?.lastSeen
                                                                ? new Date(row.session.lastSeen).toLocaleString()
                                                                : ""
                                                        }
                                                    >
                                                        {formatWhen(row.session?.lastSeen)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{rowActions(row, false)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            <Dialog open={Boolean(viewDevice)} onClose={() => setViewDevice(null)} fullWidth maxWidth="sm">
                <DialogTitle>Device ID</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {viewDevice?.email}
                        </Typography>
                        <TextField
                            value={viewDevice?.device?.deviceId || ""}
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                sx: { fontFamily: "ui-monospace, monospace", fontSize: 14 },
                            }}
                        />
                        <Typography variant="body2">
                            App version: {viewDevice?.device?.appVersion || "—"}
                        </Typography>
                        {viewDevice?.device?.companyName ? (
                            <Typography variant="body2" color="text.secondary">
                                Company: {viewDevice.device.companyName}
                            </Typography>
                        ) : null}
                        {viewDevice?.device?.lastReportedAt ? (
                            <Typography variant="caption" color="text.secondary">
                                Last reported {formatWhen(viewDevice.device.lastReportedAt)}
                                {viewDevice.device.lastReportReason ? ` (${viewDevice.device.lastReportReason})` : ""}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDevice(null)}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        disabled={!viewDevice?.device?.deviceId}
                        onClick={() => copyDeviceId(viewDevice.device.deviceId)}
                    >
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(editRow)} onClose={() => !busy && setEditRow(null)} fullWidth maxWidth="sm">
                <DialogTitle>Remap device ID</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {editRow?.email} — enter the Device ID from their new PC&apos;s MachineFlow activate
                            screen. The old PC will stop validating.
                        </Typography>
                        <TextField
                            label="Device ID"
                            value={nextDeviceId}
                            onChange={(e) => setNextDeviceId(e.target.value)}
                            autoFocus
                            fullWidth
                            helperText="Copied from MachineFlow on the replacement PC"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditRow(null)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={saveRemap} disabled={busy || !nextDeviceId.trim()}>
                        Save device ID
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(releaseRow)} onClose={() => !busy && setReleaseRow(null)} fullWidth maxWidth="sm">
                <DialogTitle>Release device binding</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                        Release {releaseRow?.email}&apos;s current Device ID
                        {releaseRow?.device?.deviceId ? ` (${shortDeviceId(releaseRow.device.deviceId)})` : ""}? They
                        can register MachineFlow on a new PC without you typing the new ID first.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReleaseRow(null)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button color="warning" variant="contained" onClick={confirmRelease} disabled={busy}>
                        Release device
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
